import twoFactorService from '@/api/two-factor-service'
import { mapErrorsToInputs } from '@/util/forms'
import i18n from '@/i18n/i18n-config'
import eventBus, { OPEN_SNACKBAR } from '@/util/event-bus'

const state = {
  loadingSetup: false,
  busy: false,
  qrSvgOrDataUrl: '',
  otpauthUrl: '',
  secret: '',
  validationErrors: {},
}

const getters = {
  qrSvgIsInline: (state) =>
    typeof state.qrSvgOrDataUrl === 'string' &&
    state.qrSvgOrDataUrl.trim().startsWith('<svg'),
}

const mutations = {
  SET_LOADING_SETUP(state, isLoading) {
    state.loadingSetup = isLoading
  },
  SET_BUSY(state, isBusy) {
    state.busy = isBusy
  },
  SET_QR(state, val) {
    state.qrSvgOrDataUrl = val || ''
  },
  SET_OTPAUTH(state, val) {
    state.otpauthUrl = val || ''
  },
  SET_SECRET(state, val) {
    state.secret = val || ''
  },

  SET_VALIDATION_ERRORS(state, errors) {
    state.validationErrors = errors || {}
  },

  RESET_STATE(state) {
    state.loadingSetup = false
    state.busy = false
    state.qrSvgOrDataUrl = ''
    state.otpauthUrl = ''
    state.secret = ''
    state.validationErrors = {}
  },
}

const actions = {
  reset({ commit }) {
    commit('RESET_STATE')
  },

  async fetchSetup({ commit }) {
    commit('SET_LOADING_SETUP', true)
    commit('SET_VALIDATION_ERRORS', {})

    try {
      const { data } = await twoFactorService.setup()
      // If the backend returns two_factor_enabled === true, no need to show setup
      if (data?.two_factor_enabled) {
        commit('SET_QR', '')
        commit('SET_OTPAUTH', '')
        commit('SET_SECRET', '')
      } else {
        commit('SET_QR', data?.qr_svg_or_data_url || '')
        commit('SET_OTPAUTH', data?.otpauth_url || '')
        commit('SET_SECRET', data?.secret || '')
      }
      return data

    } catch (e) {
        const msg = i18n.t(e?.response?.data?.error || inputs?.otp_code) || i18n.t('two_factor.load_failed')
        eventBus.$emit(OPEN_SNACKBAR, msg)

    } finally {
      commit('SET_LOADING_SETUP', false)
    }
  },

  async enable({ commit, dispatch }, otp_code) {
    commit('SET_BUSY', true)
    commit('SET_VALIDATION_ERRORS', {})

    try {
      const { data } = await twoFactorService.enable(otp_code)
      commit('SET_QR', '')
      commit('SET_OTPAUTH', '')
      const msg = i18n.t('two_factor.enabled_ok')
      eventBus.$emit(OPEN_SNACKBAR, msg)
      await dispatch('auth/getCurrentUser', null, { root: true })
      return data

    } catch (e) {
      const inputs = mapErrorsToInputs(e)
      commit('SET_VALIDATION_ERRORS', inputs)
      return null

    } finally {
      commit('SET_BUSY', false)
    }
  },

  async disable({ commit, dispatch }, payload) {
    commit('SET_BUSY', true)
    commit('SET_VALIDATION_ERRORS', {})

    try {
      await twoFactorService.disable(payload)
      const msg = i18n.t('two_factor.disabled_ok')
      eventBus.$emit(OPEN_SNACKBAR, msg)
      await dispatch('auth/getCurrentUser', null, { root: true })
      return true

    } catch (e) {
      const inputs = mapErrorsToInputs(e)
      commit('SET_VALIDATION_ERRORS', inputs)

    } finally {
      commit('SET_BUSY', false)
    }
  },

  // Separate 2FA login dialog
  async challenge({ commit }, payload) {
    commit('SET_BUSY', true)

    try {
      await twoFactorService.challenge(payload); // without Bearer
      return true; 
    } finally {
      commit('SET_BUSY', false)
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}